import { lazy } from 'react';

import { Center, Spinner } from '@chakra-ui/react';
import { Redirect } from 'react-router-dom';

import { DNS_ID, PRIVACY_ID, TOS_ID } from '@m/constants/AgreementTypes';

import AccountDeletePage from '../containers/AccountDeletePage';

import { LazyRoute } from './LazyRoute';
import {
  PublicRoutes,
  ProtectedRoutes,
  DefaultOnboardingRoutes,
  publicPaths,
  signInOnboardingPaths,
  startParkingOnboardingPaths,
  expressOnboardingPaths,
  taskItemPaths,
  referralOnboardingPaths,
} from './paths';

import type { SignInOnboardingExperimentState } from 'apps/customer/containers/Onboarding/container/useSignInOnboardingExperiment';

const PosReceiptPage = lazy(() => import('apps/customer/components/PosReceipt/PosReceiptPage'));
const PhysicalCardPage = lazy(
  () => import('apps/customer/containers/PhysicalCardPage/PhysicalCardPage'),
);
const TermsContainer = lazy(() => import('apps/customer/containers/TermsContainer'));
const HelpRouter = lazy(() => import('apps/customer/components/HelpRouter'));
const ClaimPage = lazy(() => import('apps/customer/containers/Claim'));
const ReserveParkingCheckoutPage = lazy(
  () => import('apps/customer/containers/ReserveParkingCheckout/ReserveParkingCheckout'),
);
const ReserveParkingConfirmationPage = lazy(
  () => import('apps/customer/containers/ReserveParkingConfirmation/ReserveParkingConfrimation'),
);
const DiscoveryMapContainer = lazy(
  () => import('apps/customer/containers/DiscoveryMap/DiscoveryMapContainer'),
);
const HomeContainer = lazy(() => import('apps/customer/containers/Home/HomeContainer'));

// /task handler
const PaymentOverduePage = lazy(() => import('apps/customer/containers/PaymentOverduePage'));

// onboarding
const WelcomePage = lazy(() => import('apps/customer/containers/WelcomePage'));
const ReferralOnboardingPage = lazy(() => import('apps/customer/containers/ReferralOnboarding'));
const OnboardWrapper = lazy(() => import('apps/customer/containers/OnboardingFlow/OnboardWrapper'));
const ConfirmSubscription = lazy(
  () => import('apps/customer/containers/ConfirmSubscription/ConfirmSubscription'),
);
const SubscriptionConfirmed = lazy(
  () => import('apps/customer/containers/SubscriptionConfirmed/SubscriptionConfirmed'),
);

// signed in
const MembershipContainer = lazy(() => import('apps/customer/containers/MembershipContainer'));
const ParkNowContainer = lazy(
  () => import('apps/customer/containers/ParkNowContainer/ParkNowContainer'),
);

const VisitPage = lazy(() => import('apps/customer/containers/VisitPage'));
const PaymentPage = lazy(() => import('apps/customer/containers/PaymentPage'));
const ProfileContainer = lazy(() => import('apps/customer/containers/ProfileContainer'));
const RedeemContainer = lazy(() => import('apps/customer/containers/RedeemContainer'));
const VehicleContainer = lazy(() => import('apps/customer/containers/VehicleContainer'));
const VisitReceiptPage = lazy(() => import('apps/customer/components/VisitReceiptPage'));
const ConfirmMissedEntryPage = lazy(
  () => import('apps/customer/containers/ConfirmMissedEntryPage'),
);
const DashboardPage = lazy(() => import('apps/customer/containers/Dashboard'));
const ActivityPage = lazy(() => import('apps/customer/containers/Activity/Activity'));
const ReservationActivityDetailPage = lazy(
  () => import('apps/customer/containers/ReservationActivityDetail/ReservationActivityDetail'),
);
const PromotionsPage = lazy(() => import('apps/customer/containers/Promotions'));
const QsrPromoLocationsPage = lazy(
  () => import('apps/customer/containers/QsrPromoLocations/QsrPromoLocationsPage'),
);
const RewardsPage = lazy(() => import('apps/customer/containers/Rewards'));
const LinkAccountPage = lazy(() => import('apps/customer/containers/Rewards/LinkAccount'));
const PassReceiptPage = lazy(() => import('apps/customer/containers/ReceiptPage'));
const GateAccessPage = lazy(() => import('apps/customer/containers/GateAccess/GateAccessPage'));

export function getUnauthenticatedFallback(discoveryMapV2: boolean) {
  if (discoveryMapV2) return null;
  return <Redirect to={DefaultOnboardingRoutes.SignIn} />;
}

export const onboardingRoutesList = ['/welcome']
  .concat(signInOnboardingPaths)
  .concat(publicPaths)
  .concat(referralOnboardingPaths);

export const publicRoutesList = (discoveryMapV2: boolean, physicalCardEnabled = false) => [
  {
    path: PublicRoutes.Terms,
    component: LazyRoute,
    children: <TermsContainer agreementType={TOS_ID} />,
  },
  {
    path: PublicRoutes.Privacy,
    component: LazyRoute,
    children: <TermsContainer agreementType={PRIVACY_ID} />,
  },
  {
    path: PublicRoutes.DoNotSell,
    component: LazyRoute,
    children: <TermsContainer agreementType={DNS_ID} />,
  },
  {
    exact: false,
    path: PublicRoutes.Help,
    component: LazyRoute,
    children: <HelpRouter />,
  },
  {
    exact: false,
    path: PublicRoutes.Claim,
    component: LazyRoute,
    children: <ClaimPage />,
  },
  {
    exact: true,
    path: PublicRoutes.ReserveParking,
    component: LazyRoute,
    children: <ReserveParkingCheckoutPage />,
  },
  {
    exact: true,
    path: PublicRoutes.KioskValidation,
    component: LazyRoute,
    children: (
      <Center h="100dvh">
        <Spinner size="lg" />
      </Center>
    ),
  },
  {
    exact: true as const,
    path: PublicRoutes.ParkNowV2,
    component: LazyRoute,
    children: discoveryMapV2 ? (
      <DiscoveryMapContainer />
    ) : (
      <Redirect to={PublicRoutes.ReserveParking} />
    ),
  },
  {
    exact: true,
    path: PublicRoutes.PosReceipt,
    component: LazyRoute,
    children: <PosReceiptPage />,
  },
  ...(physicalCardEnabled
    ? [
        {
          exact: true,
          path: PublicRoutes.PhysicalCard,
          component: LazyRoute,
          children: <PhysicalCardPage />,
        },
      ]
    : []),
  {
    exact: true,
    path: PublicRoutes.GateAccess,
    component: LazyRoute,
    children: <GateAccessPage />,
  },
];

export const taskRoutesList = () => [
  {
    exact: true,
    path: taskItemPaths,
    component: LazyRoute,
    children: <PaymentOverduePage />,
  },
];

function welcomeRoute({
  isInviteWelcome,
  isReferredMember,
}: {
  isInviteWelcome?: boolean | null;
  isReferredMember?: boolean | null;
}) {
  if (isReferredMember) {
    return {
      path: '/welcome',
      component: LazyRoute,
      children: <ReferralOnboardingPage />,
    };
  }
  if (isInviteWelcome) {
    return {
      path: '/welcome',
      component: LazyRoute,
      children: <WelcomePage />,
    };
  }
  return null;
}

export const customerRoutesList = ({
  isInviteWelcome,
  isReferredMember,
  hasActiveVisit,
  hasInReviewVisit,
  discoveryMapV2,
  isStpOnboarding,
  stpOnboardingExperiment,
}: {
  discoveryMapV2: boolean;
  hasActiveVisit: boolean | null;
  hasInReviewVisit: boolean;
  isInviteWelcome?: boolean | null;
  isReferredMember?: boolean | null;
  isStpOnboarding: boolean;
  stpOnboardingExperiment: SignInOnboardingExperimentState;
}) => [
  // onboarding
  welcomeRoute({ isInviteWelcome, isReferredMember }),
  {
    exact: true,
    path: [...startParkingOnboardingPaths, ...expressOnboardingPaths],
    component: LazyRoute,
    children: (
      <OnboardWrapper
        isStpOnboarding={isStpOnboarding}
        stpOnboardingExperiment={stpOnboardingExperiment}
      />
    ),
  },
  // signed in
  {
    exact: true,
    path: ProtectedRoutes.ForwardSlash,
    component: LazyRoute,
    children:
      hasActiveVisit === null ? null : (
        <Redirect
          to={(() => {
            if (hasActiveVisit) return ProtectedRoutes.VisitPage;
            // No active visit, but one is under manual-exit review -- send to Dashboard
            // (not Home) since that's the only page that surfaces that status.
            if (hasInReviewVisit) return ProtectedRoutes.DashboardPage;
            return ProtectedRoutes.HomePage;
          })()}
        />
      ),
  },
  {
    path: ProtectedRoutes.VisitPage,
    component: LazyRoute,
    exact: true,
    children: <VisitPage />,
  },
  {
    path: ProtectedRoutes.PaymentPage,
    component: LazyRoute,
    children: <PaymentPage />,
  },
  {
    exact: true,
    path: ProtectedRoutes.ProfilePage,
    component: LazyRoute,
    children: <ProfileContainer />,
  },
  {
    exact: true,
    path: ProtectedRoutes.AccountDeletePage,
    component: LazyRoute,
    children: <AccountDeletePage />,
  },
  {
    path: ProtectedRoutes.MembershipPage,
    component: LazyRoute,
    children: <MembershipContainer />,
  },
  {
    exact: true,
    path: ProtectedRoutes.HomePage,
    component: LazyRoute,
    children: <HomeContainer />,
  },
  {
    path: ProtectedRoutes.ParkNowPage,
    component: LazyRoute,
    children: discoveryMapV2 ? <DiscoveryMapContainer /> : <ParkNowContainer />,
  },
  {
    path: ProtectedRoutes.RedeemPage,
    component: LazyRoute,
    children: <RedeemContainer />,
  },
  {
    path: ProtectedRoutes.VehiclePage,
    component: LazyRoute,
    children: <VehicleContainer />,
  },
  {
    path: ProtectedRoutes.VisitReceiptPage,
    component: LazyRoute,
    exact: true,
    children: <VisitReceiptPage />,
  },
  {
    path: ProtectedRoutes.DashboardPage,
    component: LazyRoute,
    exact: true,
    children: hasActiveVisit === null ? null : <DashboardPage />,
  },
  {
    path: ProtectedRoutes.ActivityPage,
    component: LazyRoute,
    exact: true,
    children: <ActivityPage />,
  },
  {
    path: ProtectedRoutes.ReservationActivityDetailPage,
    component: LazyRoute,
    exact: true,
    children: <ReservationActivityDetailPage />,
  },
  {
    path: ProtectedRoutes.ConfirmMissedEntryPage,
    component: LazyRoute,
    exact: true,
    children: <ConfirmMissedEntryPage />,
  },
  {
    path: ProtectedRoutes.PromotionsPage,
    component: LazyRoute,
    exact: true,
    children: <PromotionsPage />,
  },
  {
    path: ProtectedRoutes.QsrPromoLocationsPage,
    component: LazyRoute,
    exact: true,
    children: <QsrPromoLocationsPage />,
  },
  {
    path: ProtectedRoutes.RewardsPage,
    component: LazyRoute,
    exact: true,
    children: <RewardsPage />,
  },
  {
    path: ProtectedRoutes.ConfirmSubscription,
    component: LazyRoute,
    exact: true,
    children: <ConfirmSubscription />,
  },
  {
    path: ProtectedRoutes.SubscriptionConfirmed,
    component: LazyRoute,
    exact: true,
    children: <SubscriptionConfirmed />,
  },
  {
    path: ProtectedRoutes.LinkAccountPage,
    component: LazyRoute,
    exact: true,
    children: <LinkAccountPage />,
  },
  {
    path: ProtectedRoutes.ReceiptPage,
    component: LazyRoute,
    exact: true,
    children: <PassReceiptPage />,
  },
  {
    exact: true,
    path: ProtectedRoutes.ReserveParkingConfirmationPage,
    component: LazyRoute,
    children: <ReserveParkingConfirmationPage />,
  },
];
